import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../model/user';
import { addGroupUsers, deleteGroupUser, getGroupUsers } from '../api/groups';
import { Program, ProgramUser } from '../model/program';
import { getProgramUsers, addUsersToProgram, removeUserFromProgram, getProgramsUsers } from '../api/programs';
import { UserPermission, UserRole } from '../auth/access';
import { getUsers, updateUsers } from '../api/users';
import { removeUserRole } from '../api/roles';
import { grantUserRole } from '../api/roles';
import { getRoleUsers } from '../api/roles';
import { getPermissionUsers, getUserPermissions, grantUserPermission, removeUserPermission } from '../api/permissions';

interface UserState {
  users: User[]
  currentGroupUsers: User[]
  currentProgramUsers: ProgramUser[] | null
  currentGroupProgramsUsers: ProgramUser[] | null
  currentClientAdmins: User[]
  currentContentEditors: User[]
}

const initialState: UserState = {
  users: [],
  currentGroupUsers: [],
  currentProgramUsers: [],
  currentGroupProgramsUsers: [],
  currentClientAdmins: [],
  currentContentEditors: []
};

const sortAlphabetical = <T extends {[key: string]: any }>(items: T[], ascending: boolean, field: string): T[] => {
  const copy = items.slice();

  copy.sort((a, b) => {
    let aValue = a[field]?.toLowerCase() ?? ""
    let bValue = b[field]?.toLowerCase() ?? ""
    
    return ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  return copy
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    sortCurrentGroupUsers: (state, action: PayloadAction<{ ascending: boolean, field: string}>) => {
      state.currentGroupUsers = sortAlphabetical(state.currentGroupUsers, action.payload.ascending, action.payload.field);
    },
    sortCurrentProgramUsersAlphabetical: (state, action: PayloadAction<{ ascending: boolean, field: string}>) => {
      if (state.currentProgramUsers == null) {
        return
      }

      state.currentProgramUsers = sortAlphabetical(state.currentProgramUsers, action.payload.ascending, action.payload.field);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.fulfilled, (state: UserState, action) => {
        state.users = action.payload;
      })
      .addCase(getUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(updateUsersAsync.fulfilled, (state: UserState, action) => {
        const updatedById = new Map(action.payload.map((u: User) => [u.id, u]));

        state.users = state.users?.map(u => {
          const updated = updatedById.get(u.id);
          return updated ? { ...u, firstName: updated.firstName, lastName: updated.lastName, department: updated.department } : u;
        }) ?? state.users;

        state.currentGroupUsers = state.currentGroupUsers?.map(u => {
          const updated = updatedById.get(u.id);
          return updated ? { ...u, firstName: updated.firstName, lastName: updated.lastName, department: updated.department } : u;
        }) ?? state.currentGroupUsers;
        
        state.currentProgramUsers = state.currentProgramUsers?.map(pu => {
          const updated = updatedById.get(pu.userId);
          return updated ? { ...pu, firstName: updated.firstName, lastName: updated.lastName, department: updated.department } : pu;
        }) ?? state.currentProgramUsers;
      })
      .addCase(updateUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getGroupUsersAsync.fulfilled, (state: UserState, action) => {
        state.currentGroupUsers = action.payload;
      })
      .addCase(getGroupUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeGroupUserAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.users = state.users.filter(user => user.id !== removedUserId);
        state.currentGroupUsers = state.currentGroupUsers.filter(user => user.id !== removedUserId);
        state.currentProgramUsers = state.currentProgramUsers?.filter(user => user.userId !== removedUserId) ?? state.currentProgramUsers;
      })
      .addCase(removeGroupUserAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getProgramUsersAsync.fulfilled, (state: UserState, action: PayloadAction<ProgramUser[]>) => {
        if (state.currentGroupUsers.length == 0) {
          state.currentProgramUsers = action.payload;
        } else {

          let programUsers: ProgramUser[] = []
          for(let programUser of action.payload) {
            for(let currentGroupUser of state.currentGroupUsers) {
              if (currentGroupUser.id == programUser.userId) {
                programUsers.push({
                  department: currentGroupUser.department,
                  firstName: currentGroupUser.firstName,
                  lastName: currentGroupUser.lastName,
                  ...programUser
                })
              }
            }
          }

          state.currentProgramUsers = programUsers;
        }
      })
      .addCase(getProgramUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getProgramsUsersAsync.fulfilled, (state: UserState, action: PayloadAction<ProgramUser[]>) => {
        state.currentGroupProgramsUsers = action.payload;
      })
      .addCase(getProgramsUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(addUsersToProgramAsync.fulfilled, (state: UserState, action: PayloadAction<ProgramUser[]>) => {
        state.currentProgramUsers = state.currentProgramUsers || [];

        let existingUserIds = new Set(state.currentProgramUsers.map(user => user.userId));
        let newProgramUsers = action.payload.filter(user => !existingUserIds.has(user.userId))
        state.currentProgramUsers.push(...newProgramUsers);
      })
      .addCase(addUsersToProgramAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeUserFromProgramAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.currentProgramUsers = state.currentProgramUsers?.filter(user => user.userId !== removedUserId) ?? null;
      })
      .addCase(removeUserFromProgramAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getRoleUsersAsync.fulfilled, (state: UserState, action: PayloadAction<User[]>) => {
        state.currentClientAdmins = action.payload
      })
      .addCase(getRoleUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      // the redux pending state is used to update frontend state, and only reversed if request is rejected
      .addCase(addGroupUsersAsync.pending, (state: UserState, action) => {
        action.meta.arg.users.forEach(argUser => {
          const existingIdx = state.currentGroupUsers.findIndex(user => user.email === argUser.email);
          if (existingIdx >= 0) {
            state.currentGroupUsers[existingIdx] = argUser;
          } else {
            state.currentGroupUsers.push(argUser);
          }
        });
      })
      .addCase(addGroupUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);

        const userIds = action.meta.arg.users.map(user => user.id);

        state.currentGroupUsers = state.currentGroupUsers.filter(user => !userIds.includes(user.id));
        state.currentProgramUsers = state.currentProgramUsers?.filter(pu => !userIds.includes(pu.userId)) ?? state.currentProgramUsers;
      })
      .addCase(addUserRoleAsync.fulfilled, (state: UserState, action) => {
        const grantedUserId = action.meta.arg.userId;
        let user = state.users.find(user => user.id == grantedUserId)
        let alreadyAdded = state.currentClientAdmins.map(admin => admin.id).includes(grantedUserId)
        
        if(user && !alreadyAdded) {
          state.currentClientAdmins.push(user);
        }
      })
      .addCase(addUserRoleAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeUserRoleAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.currentClientAdmins = state.currentClientAdmins?.filter(user => user.id !== removedUserId);
      })
      .addCase(removeUserRoleAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getPermissionUsersAsync.fulfilled, (state: UserState, action) => {
        if (action.meta.arg.permission == UserPermission.MANAGE_CONTENT) {
          state.currentContentEditors = action.payload
        }
      })
      .addCase(getPermissionUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(addUserPermissionAsync.fulfilled, (state: UserState, action) => {
        const grantedUserId = action.meta.arg.userId;
        let user = state.users.find(user => user.id == grantedUserId)
        
        let alreadyAdded = state.currentContentEditors.map(admin => admin.id).includes(grantedUserId)
        let editorPermission = action.meta.arg.permission == UserPermission.MANAGE_CONTENT

        if(user && !alreadyAdded && editorPermission) {
          state.currentContentEditors.push(user);
        }
      })
      .addCase(addUserPermissionAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeUserPermissionAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;

        const is_edit_permission = action.meta.arg.permission == UserPermission.MANAGE_CONTENT;
        if (is_edit_permission) {
          state.currentContentEditors = state.currentContentEditors?.filter(user => user.id !== removedUserId);
        }
      })
      .addCase(removeUserPermissionAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
  },
});

export const getUsersAsync = createAsyncThunk('users/getUsersAsync', async ({ accessToken }: { accessToken: string }) => {
    const response = await getUsers(accessToken);
    return response;
  }
);

export const updateUsersAsync = createAsyncThunk('program/updateUsersAsync', async ({ accessToken, users }: { accessToken: string; users: User[];}) => {
    const response = await updateUsers(accessToken, users);
    return response;
  }
);

export const getGroupUsersAsync = createAsyncThunk('users/getGroupUsersAsync', async ({ accessToken, groupId }: { accessToken: string; groupId: string}) => {
    const response = await getGroupUsers(accessToken, groupId);
    return response;
  }
);

export const removeGroupUserAsync = createAsyncThunk('users/removeGroupUserAsync', async ({ accessToken, groupId, userId }: { accessToken: string; groupId: string; userId: string;}) => {
    const response = await deleteGroupUser(accessToken, groupId, userId);
    return response;
  }
);

export const getProgramUsersAsync = createAsyncThunk('program/getProgramUsersAsync', async ({ programId, accessToken }: { programId: string; accessToken: string}) => {
    const response = await getProgramUsers(accessToken, programId);
    return response;
  }
);

export const getProgramsUsersAsync = createAsyncThunk('program/getProgramsUsersAsync', async ({ programIds, accessToken }: { programIds: string[]; accessToken: string}) => {
    const response = await getProgramsUsers(accessToken, programIds);
    return response;
  }
);

export const addUsersToProgramAsync = createAsyncThunk('program/addUsersToProgramAsync', async ({ users, program, accessToken }: { users: User[], program: Program; accessToken: string}) => {
    const response = await addUsersToProgram(accessToken, program, users);
    return response;
  }
);

export const removeUserFromProgramAsync = createAsyncThunk('program/removeUserFromProgramAsync', async ({ accessToken, programId, userId }: { accessToken: string; programId: string; userId: string;}) => {
    const response = await removeUserFromProgram(accessToken, programId, userId);
    return response;
  }
);

export const getRoleUsersAsync = createAsyncThunk('program/getRoleUsersAsync', async ({ accessToken, role }: { accessToken: string; role: UserRole;}) => {
    const response = await getRoleUsers(accessToken, role);
    return response;
  }
);

export const addGroupUsersAsync = createAsyncThunk('program/addGroupUsersAsync', async ({ accessToken, groupId, users }: { accessToken: string; groupId: string; users: User[];}) => {
    const response = await addGroupUsers(accessToken, groupId, users);
    return response;
  }
);

export const addUserRoleAsync = createAsyncThunk('program/addUserRoleAsync', async ({ accessToken, userId, role }: { accessToken: string; userId: string; role: UserRole;}) => {
    const response = await grantUserRole(accessToken, userId, role);
    return response;
  }
);

export const removeUserRoleAsync = createAsyncThunk('program/removeUserRoleAsync', async ({ accessToken, userId, role }: { accessToken: string; userId: string; role: UserRole;}) => {
    const response = await removeUserRole(accessToken, userId, role);
    return response;
  }
);


export const getPermissionUsersAsync = createAsyncThunk('program/getPermissionUsersAsync', async ({ accessToken, permission }: { accessToken: string; permission: UserPermission;}) => {
    const response = await getPermissionUsers(accessToken, permission);
    return response;
  }
);

export const getUserPermissionsAsync = createAsyncThunk('program/getUserPermissionsAsync', async ({ accessToken, userId }: { accessToken: string; userId: string;}) => {
    const response = await getUserPermissions(accessToken, userId);
    return response;
  }
);

export const addUserPermissionAsync = createAsyncThunk('program/addUserPermissionAsync', async ({ accessToken, userId, permission }: { accessToken: string; userId: string; permission: UserPermission;}) => {
    const response = await grantUserPermission(accessToken, userId, permission);
    return response;
  }
);

export const removeUserPermissionAsync = createAsyncThunk('program/removeUserPermissionAsync', async ({ accessToken, userId, permission }: { accessToken: string; userId: string; permission: UserPermission;}) => {
    const response = await removeUserPermission(accessToken, userId, permission);
    return response;
  }
);

export const { sortCurrentGroupUsers, sortCurrentProgramUsersAlphabetical } = userSlice.actions;
export default userSlice.reducer;